import { Spacer } from 'app-v2/design-systems'
import { CircleFaq, FaqBurger, FaqQuestion } from 'app/assets/icons'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { Breadcrumb } from 'app/common/breadcrumb'
import { DropdownSelect } from 'app/common/dropdown'
import { ErrorManagement } from 'app/common/error-management'
import { Layout } from 'app/common/layout'
import { fonts, H2, H4, H5, Text, TinyText } from 'app/common/typography'
import { Botmind } from 'app/faq/botmind'
import { Easiware } from 'app/faq/easiware'
import { FaqForm } from 'app/faq/form'
import { QuestionCards } from 'app/faq/questions'
import { getBotmindScript } from 'app/faq/utils'
import { useScript } from 'hooks/useScript.hooks'
import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { colors } from 'shared/assets/styles'
import { fetchFaqPageData, resetSuccess, selectTheme } from 'shared/store/faq'
import type { RootState } from 'shared/store/reducers'
import type { ThemeShortDTO } from 'shared/types'
import { featureCodes, getEasiwareTheme } from 'shared/utils'
import { getIsFromApp, getUrlParams } from 'shared/utils/ecommerce/utils'
import styled, { css } from 'styled-components'
import { FeatureToggle } from '../common/feature-toggle'
import { marketSelect } from '../common/utils'

const { yellow, red, text } = colors

const REUNION_SUPPORT_EMAIL = 'bonjour@burgerking-reunion.re'
const REUNION_MAILTO_URL = `mailto:${REUNION_SUPPORT_EMAIL}`

const Faq = () => {
    const { themes, selectedTheme, error, networkError } = useSelector(
        (state: RootState) => state.faqPageState
    )
    const { email, firstname, lastname } = useSelector(
        (state: RootState) => state.kingdomProfilePageState
    )
    const { cookiesConsentsDone } = useSelector(
        (state: RootState) => state.userState
    )
    const [shouldBeDisplayed, setShouldBeDisplayed] = useState(false)
    const dispatch = useDispatch()
    const intl = useIntl()
    const scrollRef = useRef()
    const [ecommerceMode, setEcommerceMode] = useState('')
    const params = getUrlParams(window.location.href)

    const botmindScript = getBotmindScript(
        email,
        firstname,
        lastname,
        `${!!cookiesConsentsDone}`
    )
    useScript({
        innerHTML: botmindScript
    })

    useEffect(() => {
        dispatch(fetchFaqPageData())
        if (scrollRef.current && params?.themeParamKey) {
            handleSelectTheme(intl.formatMessage({ id: params.themeParamKey }))

            scrollRef.current.scrollIntoView({
                behavior: 'smooth'
            })
        }

        return () => {
            dispatch(resetSuccess())
        }
    }, [])

    const handleSelectTheme = (value: string) => {
        setShouldBeDisplayed(false)
        dispatch(selectTheme(value))
        setEcommerceMode('')
        // We need to add this timeout to fix an issue with Easiware - BE-6733
        setTimeout(() => setShouldBeDisplayed(true), 200)
    }

    const mapThemesToSelectOptions = () =>
        themes.map((c: ThemeShortDTO) => {
            return {
                code: c.label,
                label: c.label
            }
        })
    const faqTitle = intl.formatMessage({ id: 'faq.title' })
    const isFromApp = getIsFromApp(params)
    const easiwareTheme = isFromApp
        ? params?.easiwareTheme
        : getEasiwareTheme(selectedTheme, ecommerceMode)

    const { botmindTheme } = selectedTheme || {}

    return isFromApp && !!easiwareTheme ? (
        <Easiware theme={easiwareTheme} />
    ) : (
        <ErrorManagement error={error} networkError={networkError}>
            <Layout background="plain" border>
                <StyledContainer>
                    <HeadContainer>
                        <FaqHead>
                            <Breadcrumb
                                breadcrumbArray={[
                                    {
                                        title: `\u00A0>\u00A0${faqTitle}`
                                    }
                                ]}
                            />
                            <StyledH2>{faqTitle}</StyledH2>
                            <Baseline>
                                <Text>
                                    {intl.formatMessage({
                                        id: 'faq.baseline'
                                    })}
                                </Text>
                            </Baseline>
                        </FaqHead>
                    </HeadContainer>

                    <QuestionCards />
                </StyledContainer>
                <FeatureToggle feature={featureCodes.CONTACT_FORM}>
                    <FormChoice ref={scrollRef}>
                        <BurgerIconContainer>
                            <FaqBurger />
                        </BurgerIconContainer>
                        {themes && (
                            <DropdownContainer>
                                <CircleFaqContainer>
                                    <CircleFaq color={yellow} />
                                </CircleFaqContainer>
                                <ResponsiveContainer>
                                    <H4 color="white">
                                        {intl.formatMessage({
                                            id: 'faq.theme.main.baseline'
                                        })}
                                    </H4>

                                    <>
                                        {marketSelect({
                                            reunion: (
                                                <StyledTextContainer>
                                                    <H5
                                                        color="white"
                                                        font={fonts.roboto}>
                                                        {intl.formatMessage({
                                                            id:
                                                                'faq.theme.contact-us'
                                                        })}
                                                    </H5>
                                                    <a
                                                        href={
                                                            REUNION_MAILTO_URL
                                                        }>
                                                        <H5
                                                            color="white"
                                                            font={fonts.roboto}>
                                                            {
                                                                REUNION_SUPPORT_EMAIL
                                                            }
                                                        </H5>
                                                    </a>
                                                </StyledTextContainer>
                                            ),
                                            france: (
                                                <H5
                                                    color="white"
                                                    font={fonts.roboto}>
                                                    {intl.formatMessage({
                                                        id:
                                                            'faq.theme.second.baseline'
                                                    })}
                                                </H5>
                                            )
                                        })}
                                    </>
                                </ResponsiveContainer>
                                {marketSelect({
                                    reunion: <></>,
                                    france: (
                                        <DropdownSelectContainer>
                                            <DropdownSelect
                                                selectOptions={mapThemesToSelectOptions()}
                                                color={text}
                                                defaultValue={
                                                    selectedTheme
                                                        ? selectedTheme?.label
                                                        : '-1'
                                                }
                                                handleChange={handleSelectTheme}
                                                placeholderKey="faq.theme.placeholder"
                                                background="white"
                                            />
                                        </DropdownSelectContainer>
                                    )
                                })}
                            </DropdownContainer>
                        )}
                        <QuestionIconContainer>
                            <FaqQuestion />
                        </QuestionIconContainer>
                    </FormChoice>
                    {shouldBeDisplayed && (
                        <FormContainer>
                            {!!easiwareTheme ? (
                                <Easiware theme={easiwareTheme} />
                            ) : !!botmindTheme ? (
                                <Botmind theme={botmindTheme} />
                            ) : (
                                <FaqForm
                                    ecommerceMode={ecommerceMode}
                                    selectEcommerceMode={(
                                        ecommerceMode: string
                                    ) => setEcommerceMode(ecommerceMode)}
                                />
                            )}
                        </FormContainer>
                    )}
                </FeatureToggle>
            </Layout>
        </ErrorManagement>
    )
}

const StyledContainer = styled(ResponsiveContainer)`
    margin-top: 90px;
    ${media.medium`
        margin-top: 150px;
    `};
`
const FormContainer = styled(ResponsiveContainer)`
    position: relative;
`

const StyledH2 = styled(H2)`
    margin-top: 10px;
`

const Baseline = styled.p`
    margin-top: 15px;
`

const FaqHead = styled.div``

const HeadContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    align-items: flex-start;
    flex-direction: column;
    ${media.medium`
        justify-content: space-between;
        flex-direction: row;
        width: 100%;
        align-items: center;
    `};
`

const FormChoice = styled.div`
    width: 100%;
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;
    background-color: ${yellow};
    padding: 30px 0;
    ${media.medium`
        padding: 60px 0;
    `};
`

const DropdownContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    ${H4} {
        text-align: center;
    }

    ${H5} {
        text-align: center;
        text-transform: none;
    }
`
const DropdownSelectContainer = styled.div`
    margin-top: 30px;
`

const faqChoiceIcons = css`
    display: none;
    ${media.medium`
        display: block;
        position: absolute;
        transform: translateY(-50%);
        top: 50%;
    `};
`

const BurgerIconContainer = styled.div`
    ${faqChoiceIcons};
    left: -30px;
`
const QuestionIconContainer = styled.div`
    ${faqChoiceIcons};
    right: -30px;
`

const CircleFaqContainer = styled.div`
    margin-bottom: 20px;
`

const StyledTextContainer = styled.div`
    display: flex;
    justify-content: center;
`

export { Faq }
